import { Page } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { usersApi } from 'apis/users';
import {
  NOTIFICATION_DEFAULT,
  OFFER_NOTIFICATION_INCOMING,
} from 'components/notifications/sagas/audio';
import { actions as personalActions } from 'components/settings/actions/personal';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUser } from 'utils/user';

import { profileActions } from '../../settingsProfile.actions';

import { ProfileFormInfoOld } from './components/info/profileFormInfoOld';
import { ProfileFormSettings } from './components/settings/profileFormSettings';
import { ProfileFormSounds } from './components/sounds/profileFormSounds';
import { ProfileFormFieldValues } from './profileForm.types';

export const SettingsProfileFormOld = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { user } = useCurrentUser();
  const soundsState = useAppSelector((state) => state.settings.personal.sounds);

  const form = useForm<ProfileFormFieldValues>({
    mode: 'all',
    defaultValues: {
      given_name: user.given_name ?? '',
      family_name: user.family_name ?? '',
      name: user.name ?? '',
      email: user.email ?? '',
      phone_number: user.phone_number ?? '',
      offer_notification_phone: [
        soundsState?.pstnphone?.tone ?? OFFER_NOTIFICATION_INCOMING,
      ],
      offer_notification_chat: [
        soundsState?.widgetchat?.tone ?? OFFER_NOTIFICATION_INCOMING,
      ],
      offer_notification_email: [
        soundsState?.email?.tone ?? OFFER_NOTIFICATION_INCOMING,
      ],
      offer_notification_messaging: [
        soundsState?.messaging?.tone ?? OFFER_NOTIFICATION_INCOMING,
      ],
      notification_sound: [
        soundsState?.notification?.tone ?? NOTIFICATION_DEFAULT,
      ],
    },
  });

  const [updateUser] = usersApi.useUpdateUserMutation();

  const handleValidSubmit: SubmitHandler<ProfileFormFieldValues> = async (
    values,
  ) => {
    try {
      const { data } = await updateUser({
        id: user.id,
        patch: {
          updates: {
            display_name: values.name,
            given_name: values.given_name,
            family_name: values.family_name,
            ...(values.phone_number && { phone_number: values.phone_number }),
            ...(values.default_outbound_number?.length && {
              default_outbound_number:
                values.default_outbound_number[0].e164_format,
            }),
          },
          deletes: [
            ...(values.phone_number ? [] : (['phone_number'] as const)),
            ...(values.default_outbound_number?.length
              ? []
              : (['default_outbound_number'] as const)),
          ],
        },
      }).unwrap();

      dispatch(profileActions.update({ user: data }));

      dispatch(
        personalActions.personalSound({
          key: 'sounds',
          value: {
            pstnphone: {
              tone: values.offer_notification_phone[0],
            },
            widgetchat: {
              tone: values.offer_notification_chat[0],
            },
            email: {
              tone: values.offer_notification_email[0],
            },
            messaging: {
              tone: values.offer_notification_messaging[0],
            },
            notification: {
              tone: values.notification_sound[0],
            },
          },
        }),
      );

      ampli.settingsProfileFormSubmitted({
        payload: {},
      });

      notify(
        {
          title: 'Success',
          text: `Your profile has been updated.`,
          icon: 'done',
          time: 5000,
        },
        NotificationType.Success,
      );
    } catch (error) {
      notify(
        {
          title: 'Error while saving your profile.',
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <Page>
      <Page.Head title="Profile" />

      <Page.Body>
        <FormProvider {...form}>
          <form
            id={settingsProfileFormId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(handleValidSubmit)}
          >
            <ProfileFormInfoOld />
            <ProfileFormSounds />
            <ProfileFormSettings />
          </form>
        </FormProvider>
      </Page.Body>
    </Page>
  );
};

export const settingsProfileFormId = 'profile';
